import React from 'react';
const { useState, useEffect } = React;
import { ROLES, SERVICE_NAMES, t } from '../../diagnosticConfig.js';
import { COUNTRY_LIST } from '../../pricing.js';
import { adminCall, cardSt, labelSt, mutedSt, badgeSt, warnSt, textareaSt, inputSt, tableSt, thSt, tdSt, fmtDate } from './shared.jsx';
import { checkWarnings, StatusBadge, GATE_LABELS } from './AiPipeline.jsx';
import MethodUsedNote, { useMethodTitles, MethodLibraryRecord } from '../MethodUsedNote.jsx';

// Entregables: the profiles (seller and founder version of each person) and the
// founder's report. Ale edits the text of a draft (each save is a new version;
// the tables built by code are rebuilt from the validated data and the checks
// run again), approves it (confirming the warnings, if any) and publishes one
// link per deliverable, which she can revoke. No email is sent: Ale copies the
// link. Below: the follow-up proposal, closing the diagnostic and deleting a test one.

const STATES = {
  none: ['Sin correr', 'var(--globant-muted)'], running: ['⏳ Corriendo', '#2D5F99'], draft: ['Borrador', '#9A6212'],
  approved: ['✓ Aprobada', 'var(--globant-green)'], published: ['🔗 Publicada', '#818cf8'], opened: ['👁 Abierta', '#1E7B4F'],
  failed: ['✗ Falló', '#B4362C'], interrupted: ['✗ Interrumpida', '#B4362C'],
};
const TITLES = { seller: 'Ficha vendedor', founder: 'Ficha founder', report: 'Informe del founder' };
const linkFor = (token) => `${window.location.origin}/entregable?token=${token}`;

async function copy(text) {
  try { await navigator.clipboard.writeText(text); window.__oikeToast?.('Link copiado', 'success'); }
  catch { window.prompt('Copia el link:', text); }
}

function openHtml(html) {
  const win = window.open('', '_blank');
  if (!win) { window.alert('Pop-up bloqueado: permite pop-ups para esta página e intenta de nuevo.'); return; }
  win.document.write(html);
  win.document.close();
}

function StateBadge({ view }) {
  const [label, color] = STATES[view.state] || [view.state, 'var(--globant-muted)'];
  const p = view.current?.publication;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ ...badgeSt(false), color }}>{label}</span>
      {view.state === 'opened' && <span style={{ ...mutedSt, fontSize: 10 }}>el {fmtDate(p.firstOpenedAt, true)}</span>}
      {view.state === 'approved' && p?.revokedAt && <span style={{ ...mutedSt, fontSize: 10 }}>link revocado el {fmtDate(p.revokedAt)}</span>}
    </span>
  );
}

function Cell({ view, selected, onOpen, titles }) {
  const cur = view.current;
  const warnings = checkWarnings(cur?.checks).length;
  return (
    <td style={{ ...tdSt, ...(selected ? { background: 'rgba(91,191,181,0.08)' } : {}) }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <StateBadge view={view} />
        {cur && <span style={mutedSt}>v{cur.version}{cur.editedBy ? ' · editada' : ''}</span>}
        {warnings > 0 && view.state === 'draft' && <span style={{ ...badgeSt(false), color: '#9A6212' }}>⚠️ {warnings}</span>}
        {cur?.stale && <span style={{ ...badgeSt(false), color: '#9A6212' }}>Desactualizado</span>}
      </div>
      {!cur && view.gate && <div style={{ ...mutedSt, fontSize: 11, marginTop: 4 }}>{GATE_LABELS[view.gate] || view.gate}</div>}
      {cur && cur.status !== 'running' && <MethodUsedNote method={cur.methodLibrary} titles={titles} expected />}
      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
        {cur && cur.status !== 'running' && <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => onOpen(cur.id)}>Abrir</button>}
        {cur?.published && <button className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => copy(linkFor(cur.publication.token))}>🔗 Copiar link</button>}
      </div>
    </td>
  );
}

export default function Deliverables({ diagnosticId, run, busy, onDeleted }) {
  const [data, setData] = useState(null);
  const [output, setOutput] = useState(null);
  const titles = useMethodTitles();

  const load = () => run(async () => setData(await adminCall({ action: 'deliverables', diagnosticId })));
  useEffect(() => { load(); }, [diagnosticId]);

  const open = (outputId) => run(async () => setOutput(await adminCall({ action: 'getOutput', outputId })));
  // An action on the open output: refresh it (or the one it created) and the table
  const act = (body, next = (r) => r) => run(async () => {
    const r = await adminCall(body);
    const out = next(r);
    if (out?.id) setOutput(out);
    else if (output) setOutput(await adminCall({ action: 'getOutput', outputId: output.id }));
    setData(r.people ? r : await adminCall({ action: 'deliverables', diagnosticId }));
  });

  if (!data) return null;
  const d = data.diagnostic;
  const sel = output?.id;

  return (
    <div>
      {d.isTest && <div style={{ ...warnSt, fontSize: 13 }}>🧪 Diagnóstico de prueba: todas las páginas publicadas llevan la marca "Ejemplo con datos ficticios".</div>}
      {d.closed && <div style={{ ...warnSt, fontSize: 13 }}>🔒 Diagnóstico cerrado el {fmtDate(d.closedAt)}. Quedan los entregables aprobados y los agregados; los links publicados siguen funcionando.</div>}

      <div style={cardSt}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div style={labelSt}>Entregables</div>
          <span style={mutedSt}>
            Informe entregado: {d.reportDeliveredAt ? fmtDate(d.reportDeliveredAt) : '—'} ·{' '}
            <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={load} disabled={busy}>↻ Actualizar</button>
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableSt}>
            <thead><tr><th style={thSt}>Persona</th><th style={thSt}>{TITLES.seller} (solo para esa persona)</th><th style={thSt}>{TITLES.founder} (solo para el founder)</th></tr></thead>
            <tbody>
              {data.people.map(p => (
                <tr key={p.id}>
                  <td style={tdSt}><div style={{ fontWeight: 600 }}>{p.name}</div><div style={mutedSt}>{t(ROLES[p.role]?.label) || p.role}</div></td>
                  <Cell view={p.seller} selected={p.seller.current?.id === sel} onOpen={open} titles={titles} />
                  <Cell view={p.founder} selected={p.founder.current?.id === sel} onOpen={open} titles={titles} />
                </tr>
              ))}
              <tr>
                <td style={tdSt}><div style={{ fontWeight: 600 }}>{TITLES.report}</div><div style={mutedSt}>Solo para el founder</div></td>
                <Cell view={data.report} selected={data.report.current?.id === sel} onOpen={open} titles={titles} />
                <td style={tdSt} />
              </tr>
            </tbody>
          </table>
        </div>
        {!data.people.length && <div style={mutedSt}>Todavía no hay personas evaluadas.</div>}
        <div style={{ ...mutedSt, marginTop: 8 }}>Las fichas y el informe se generan en la pestaña IA (P3 y P5). No se envía ningún email: copia el link y compártelo tú.</div>
      </div>

      {output && <DeliverableDetail key={`${output.id}:${output.status}:${output.publication?.token || ''}:${output.publication?.revokedAt || ''}`}
        output={output} busy={busy} act={act} open={open} run={run} titles={titles} onClose={() => setOutput(null)} />}

      <NextService data={data} busy={busy} run={run} setData={setData} diagnosticId={diagnosticId} />
      <Management data={data} busy={busy} run={run} setData={setData} onDeleted={onDeleted} />
    </div>
  );
}

function DeliverableDetail({ output, busy, act, open, run, titles, onClose }) {
  const kind = output.step === 'P5' ? 'report' : output.variant;
  const isEditable = output.isCurrent && output.status === 'draft' && !output.closed && !output.stale;
  const [text, setText] = useState(output.content || '');
  const changed = text !== (output.content || '');
  const warnings = checkWarnings(output.checks);
  const pub = output.publication || {};

  const save = () => act({ action: 'saveEdit', outputId: output.id, baseVersion: output.version, content: text });
  const approve = () => {
    if (changed) { window.alert('Primero guarda los cambios como versión nueva.'); return; }
    if (warnings.length) {
      const ok = window.confirm(`Esta versión tiene avisos de los controles automáticos:\n\n${warnings.map(w => `• ${w}`).join('\n')}\n\n¿La apruebas igual?`);
      if (!ok) return;
    }
    act({ action: 'approveDeliverable', outputId: output.id, confirmWarnings: warnings.length > 0 });
  };
  const publish = () => {
    const who = kind === 'seller' ? `solo para ${output.person?.name || 'esta persona'}` : 'solo para el founder';
    const extra = kind === 'report' ? '\n\nPublicar el informe completa la fecha de entrega (Report Delivered At): desde ahí cuentan los 30 días hasta el cierre.' : '';
    if (window.confirm(`Se crea un link ${who}. Cualquiera con el link puede abrirlo: compártelo solo con quien corresponde.${extra}\n\n¿Publicar?`)) {
      act({ action: 'publishDeliverable', outputId: output.id });
    }
  };
  const revoke = () => window.confirm('El link deja de funcionar ya mismo y no se puede reactivar (si vuelves a publicar, se crea un link nuevo). ¿Revocar?')
    && act({ action: 'revokeDeliverable', outputId: output.id });
  const preview = () => run(async () => openHtml((await adminCall({ action: 'renderOutput', outputId: output.id })).html));

  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{TITLES[kind]}{output.person ? ` · ${output.person.name}` : ''} <span style={mutedSt}>v{output.version}</span></div>
          <div style={{ ...mutedSt, marginTop: 4 }}>
            <StatusBadge status={output.status} />{' '}
            {output.editedBy ? `Editada por ${output.editedBy} el ${fmtDate(output.editedAt, true)}` : `IA (${output.model || '—'}) · ${fmtDate(output.finishedAt || output.created, true)}`}
            {output.approvedAt && ` · Aprobada por ${output.approvedBy || '—'} el ${fmtDate(output.approvedAt, true)}`}
            {!output.isCurrent && ' · Versión anterior (solo lectura)'}
          </div>
          {output.published && <div style={{ ...mutedSt, marginTop: 4 }}>Publicada por {pub.publishedBy || '—'} el {fmtDate(pub.publishedAt, true)} · {pub.firstOpenedAt ? `abierta por primera vez el ${fmtDate(pub.firstOpenedAt, true)}` : 'todavía no se abrió'}</div>}
          {!output.published && pub.revokedAt && <div style={{ ...mutedSt, marginTop: 4 }}>Link revocado el {fmtDate(pub.revokedAt, true)}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={preview}>👁 Vista previa</button>
          {isEditable && <button className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={busy || changed} onClick={approve}>✓ Aprobar</button>}
          {output.isCurrent && output.status === 'approved' && !output.published && !output.closed && (
            <button className="action-btn" style={{ fontSize: 12 }} disabled={busy}
              onClick={() => window.confirm('Vuelve a borrador para editarlo. ¿Seguimos?') && act({ action: 'reopenOutput', outputId: output.id })}>↺ Reabrir</button>
          )}
          {output.isCurrent && output.status === 'approved' && !output.published && (
            <button className="action-btn" style={{ fontSize: 12, background: '#6366f1', color: '#fff', fontWeight: 700 }} disabled={busy} onClick={publish}>🚀 Publicar</button>
          )}
          {output.published && <button className="action-btn" style={{ fontSize: 12 }} onClick={() => copy(linkFor(pub.token))}>🔗 Copiar link</button>}
          {output.published && <button className="action-btn" style={{ fontSize: 12, color: '#B4362C' }} disabled={busy} onClick={revoke}>⛔ Revocar link</button>}
          <button className="action-btn" style={{ fontSize: 12 }} onClick={onClose}>Cerrar</button>
        </div>
      </div>

      <MethodLibraryRecord output={output} titles={titles} />
      {output.stale && <div style={{ ...warnSt, fontSize: 13 }}>Se generó con una versión anterior de un paso previo (P2 o P4). Vuelve a correrlo en la pestaña IA antes de editarlo o aprobarlo.</div>}
      {warnings.length > 0 && (
        <div style={{ ...warnSt, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Avisos de los controles automáticos {output.status === 'draft' ? '(se vuelven a calcular al guardar)' : ''}</div>
          {warnings.map((x, i) => <div key={i}>• {x}</div>)}
        </div>
      )}

      {isEditable ? (
        <>
          <div style={{ ...mutedSt, marginBottom: 6 }}>
            Edita el texto en Markdown. Las tablas (aptitudes{kind === 'report' ? ', scorecard, funnel, mapa del equipo, clima y herramientas' : ''}) las arma el código: al guardar se regeneran desde los datos validados y cualquier cambio a mano en ellas se descarta.
            Guardar crea una versión nueva; la anterior queda en el historial.
          </div>
          <textarea style={{ ...textareaSt, minHeight: 420, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, lineHeight: 1.5 }}
            value={text} onChange={e => setText(e.target.value)} maxLength={100000} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            {changed && <button className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={() => setText(output.content || '')}>Descartar cambios</button>}
            <button className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !changed || !text.trim()} onClick={save}>💾 Guardar versión nueva</button>
          </div>
        </>
      ) : (
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, background: 'rgba(30,42,44,0.03)', borderRadius: 8, padding: 12 }}>{output.content}</div>
      )}

      {output.history?.length > 1 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ ...labelSt, marginBottom: 6 }}>Historial</div>
          {output.history.map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--globant-border)', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>v{h.version}</span>
              <StatusBadge status={h.status} />
              <span style={mutedSt}>{h.editedBy ? `Editada por ${h.editedBy} · ${fmtDate(h.editedAt, true)}` : `IA${h.model ? ` (${h.model})` : ''} · ${fmtDate(h.created, true)}`}</span>
              {h.id !== output.id && <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} disabled={busy} onClick={() => open(h.id)}>Ver</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NextService({ data, busy, run, setData, diagnosticId }) {
  const n = data.nextService;
  const [needCountry, setNeedCountry] = useState(false);
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');
  // Errors of the proposal stay in this card: they never break the rest of the tab
  const create = () => run(async () => {
    setError('');
    try {
      setData(await adminCall({ action: 'createProposal', diagnosticId, ...(country ? { country } : {}) }));
      setNeedCountry(false);
      window.__oikeToast?.('Propuesta creada como borrador en la pestaña Intake de la cuenta', 'success');
    } catch (e) {
      if (e.code === 'invalid_input: missing country') setNeedCountry(true);
      setError(e.message || 'No se pudo crear la propuesta.');
    }
  });
  const q = n.quote;
  return (
    <div style={cardSt}>
      <div style={{ ...labelSt, marginBottom: 8 }}>Siguiente servicio</div>
      {n.error && <div style={{ ...warnSt, fontSize: 13 }}>No se pudieron leer los datos de la propuesta (servicio de P4 o cotizaciones de la cuenta). El resto de la pestaña funciona; puedes intentar crearla igual.</div>}
      {error && <div style={{ ...warnSt, fontSize: 13, borderColor: 'rgba(239,68,68,0.5)', color: '#B4362C' }}>⚠️ {error}</div>}
      {!n.p4Approved && !n.error ? <div style={mutedSt}>Primero valida y aprueba las dimensiones (P4): ahí se confirma el servicio recomendado.</div> : (
        <>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            Servicio confirmado en P4: <b>{n.service ? SERVICE_NAMES[n.service] || n.service : '—'}</b>{!n.confirmed && ' (sin confirmar)'} · Personas del equipo: <b>{n.teamSize}</b>
            <span style={mutedSt}> (evaluadas, sin las excluidas)</span>
          </div>
          {q && (
            <div style={{ ...mutedSt, marginBottom: 8 }}>
              Propuesta: {q.name} · {q.published ? 'publicada' : q.status === 'Approved' ? 'aprobada' : 'borrador'}. Se revisa, aprueba y publica en la pestaña Intake de la cuenta.
            </div>
          )}
          {needCountry && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13 }}>No se detectó el país del cliente:</span>
              <select style={{ ...inputSt, width: 220 }} value={country} onChange={e => setCountry(e.target.value)}>
                <option value="">Elegir país…</option>
                {COUNTRY_LIST.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          )}
          <button className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={busy || (!n.confirmed && !n.error) || (needCountry && !country) || q?.published || q?.status === 'Approved'}
            onClick={create}>{q && !q.published && q.status !== 'Approved' ? '↻ Recalcular propuesta' : '📄 Crear propuesta'}</button>
          <div style={{ ...mutedSt, marginTop: 6 }}>Usa el motor de precios con el servicio confirmado, el país del cliente y la cantidad real de personas. Queda como borrador, sin enviarse.</div>
        </>
      )}
    </div>
  );
}

const CLOSE_TEXT = `Cerrar el diagnóstico borra, en todo el equipo:
• Las transcripciones de las 1:1.
• Las respuestas de la Parte 3 de las autoevaluaciones (motivación).
• De cada sesión 1:1: el riesgo de salida, las notas de Ale, la hoja del role-play y los puntajes preliminares. Quedan solo la fecha, si fue grabada y el estado.
• Las frases textuales de todas las salidas de P1 (evidencia, citas de energía, canal y motivadores, señales de riesgo de salida y contradicciones), incluidas las versiones reemplazadas. Quedan los niveles, la energía y las fuentes.
• Las versiones de las fichas y del informe que no estén aprobadas (borradores y reemplazadas).

Quedan los entregables aprobados (y sus links publicados) y los agregados.

Importante: Airtable conserva lo borrado en su papelera por un tiempo, y el historial de cambios de cada registro puede seguir mostrando los valores anteriores de los campos vaciados. Para borrarlo del todo, hay que vaciar también la papelera de la base.

No se puede deshacer. ¿Cerrar el diagnóstico?`;

function Management({ data, busy, run, setData, onDeleted }) {
  const d = data.diagnostic;
  const close = () => {
    if (!window.confirm(CLOSE_TEXT)) return;
    run(async () => {
      const r = await adminCall({ action: 'closeDiagnostic', diagnosticId: d.id, confirm: true });
      setData(r);
      window.__oikeToast?.('Diagnóstico cerrado', 'success');
    });
  };
  const remove = () => {
    const name = window.prompt(`Esto borra el diagnóstico de prueba y todo lo vinculado: participantes, respuestas, sesiones, transcripciones, salidas de la IA, links publicados y la propuesta de seguimiento. No se puede deshacer (Airtable conserva lo borrado en su papelera por un tiempo).\n\nPara confirmar, escribe el nombre del diagnóstico:\n${d.name}`);
    if (name === null) return;
    run(async () => {
      await adminCall({ action: 'deleteTestDiagnostic', diagnosticId: d.id, confirmName: name });
      window.__oikeToast?.('Diagnóstico de prueba borrado', 'success');
      onDeleted?.();
    });
  };
  return (
    <div style={cardSt}>
      <div style={{ ...labelSt, marginBottom: 8 }}>Cierre del diagnóstico</div>
      {d.closed ? <div style={mutedSt}>Cerrado el {fmtDate(d.closedAt)}.</div> : (
        <>
          {d.closeSuggested && (
            <div style={{ ...warnSt, fontSize: 13 }}>Pasaron {d.daysSinceDelivery} días desde la entrega del informe: corresponde cerrar el diagnóstico y borrar los datos individuales.</div>
          )}
          <div style={{ ...mutedSt, marginBottom: 8 }}>
            {d.reportDeliveredAt
              ? `Informe entregado el ${fmtDate(d.reportDeliveredAt)}${d.daysSinceDelivery !== null ? ` (hace ${d.daysSinceDelivery} días)` : ''}. Se sugiere cerrar a los 30 días.`
              : 'Se puede cerrar después de publicar el informe del founder (Report Delivered At).'}
          </div>
          <button className="action-btn" style={{ fontSize: 12, ...(d.closeSuggested ? { background: '#9A6212', color: '#FFFFFF', fontWeight: 700 } : {}) }}
            disabled={busy || !d.reportDeliveredAt} onClick={close}>🔒 Cerrar diagnóstico</button>
        </>
      )}
      {d.isTest && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--globant-border)' }}>
          <button className="action-btn" style={{ fontSize: 12, color: '#B4362C' }} disabled={busy} onClick={remove}>🗑 Borrar diagnóstico de prueba</button>
          <div style={{ ...mutedSt, marginTop: 6 }}>Solo para diagnósticos de prueba. Borra el diagnóstico y todo lo vinculado.</div>
        </div>
      )}
    </div>
  );
}
